import type { AppNavParams } from '@euler/app/Routes';
import { useCurrentStore } from '@euler/app/flows/auth/context';
import { kSubscriptionStatusCached } from '@euler/app/flows/common/functions/useNavigateToTaskListItem';
import { useShowQrcodeSharingMoreActions } from '@euler/app/flows/common/functions/useShowQrcodeSharingMoreActions';
import { useOrderDetailContext } from '@euler/app/flows/order/components/OrderDetailProvider';
import { OrderPageCapabilities } from '@euler/app/flows/order/types';
import type { TaskNavParams } from '@euler/app/flows/task/TaskFlow';
import { useTaskContext } from '@euler/app/flows/task/functions/TaskContext';
import { siteInspectionToStaged } from '@euler/app/flows/task/functions/task-detail/util';
import { useTaskExtraInfo } from '@euler/app/flows/task/functions/useTaskExtraInfo';
import { useSystemMetrics } from '@euler/functions/useSystemMetrics';
import { CommonTaskStatus, InspectionTaskJobStatus } from '@euler/model/enum';
import { SiteInspectionStaged } from '@euler/model/task-detail';
import { usePersistFn } from '@euler/utils/hooks';
import { useNavigation } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RefObject, memo } from 'react';
import { View } from 'react-native';
import invariant from 'tiny-invariant';
import { OrderSummaryCard } from '../../../../../components/order/summary/OrderSummaryCard';

export const WiredTaskDetailPageOrderSummary = memo(
  ({
    hideExtendedProps,
    serviceRecordCount,
    onShowDetail,
  }: {
    hideExtendedProps?: boolean;
    serviceRecordCount: number;
    onShowDetail?: () => void;
  }) => {
    const navigation =
      useNavigation<StackNavigationProp<TaskNavParams & AppNavParams>>();

    const { navBarHeight } = useSystemMetrics();
    const { orderDetail } = useOrderDetailContext();
    const { taskNo, detail, inventoryQuery } = useTaskContext();

    const store = useCurrentStore();
    const basicInfo = detail.basicInfo;
    const extraInfo = useTaskExtraInfo();
    const subscribed =
      extraInfo?.observes == null
        ? kSubscriptionStatusCached.get(`task/${taskNo}`) ?? false
        : extraInfo.observes.length > 0;

    const onEditPress = usePersistFn(() => {
      const preInspection: SiteInspectionStaged[] = [];
      for (const siteInspection of detail.preInspection.sites) {
        const site = inventoryQuery.getSiteById(siteInspection.siteId);
        if (!site) continue;
        const staged = siteInspectionToStaged(
          siteInspection.flowId!,
          site,
          siteInspection.inspectionType,
          siteInspection,
          siteInspection.technicianId,
          siteInspection.technicianName,
        );
        preInspection.push(staged);
      }
      const capabilities: OrderPageCapabilities = {
        readonlyInspectionSubjects: new Set(),
        readonlyConstructionSubjects: new Set(),
        preInspectionReadonly:
          detail.preInspection.status === CommonTaskStatus.Finished,
      };
      for (const flow of detail.inspection.flows) {
        if (flow.status !== 'pending') {
          capabilities.readonlyInspectionSubjects.add(flow.name);
        }
      }
      for (const job of detail.construction.jobs) {
        if (job.status !== InspectionTaskJobStatus.Pending) {
          capabilities.readonlyConstructionSubjects.add(job.name);
        }
      }

      if (!detail.basicInfo.isMainOrder) {
        invariant(detail.basicInfo.parentOrderNo);
      }

      navigation.navigate('_order', {
        mode: 'update',
        basicInfo: detail.basicInfo,
        preInspection,
        capabilities,
        ...(detail.basicInfo.isMainOrder
          ? {
              type: 'main',
            }
          : {
              type: 'child',
              mainOrderNo: detail.basicInfo.parentOrderNo!,
            }),
      });
    });

    const showMoreActions = useShowQrcodeSharingMoreActions({
      onViewSubscriptions: () => {
        navigation.navigate('_vehicleSubscriptions', {
          licensePlateNo: detail.basicInfo.licensePlateNo,
        });
      },
    });

    const onQrcodeSharingPress = usePersistFn((buttonRef: RefObject<View>) => {
      navigation.navigate('qrcodeSharing', {
        taskNo,
        orderNo: detail.basicInfo.orderNo,
        reportNo: detail.basicInfo.reportNo,
        licensePlateNo: detail.basicInfo.licensePlateNo,
        storeName: store?.name ?? '',
        top: navBarHeight,
        onClose: origin => {
          if (origin === 'more') {
            showMoreActions({ anchor: buttonRef });
          }
        },
      });
    });

    const onServiceRecordsPress = usePersistFn(() => {
      navigation.navigate('records', {
        licensePlateNo: basicInfo.licensePlateNo,
        vin: basicInfo.vin,
      });
    });

    return (
      <>
        <OrderSummaryCard
          orderDetail={orderDetail}
          isMainOrder={basicInfo.isMainOrder}
          isFirstTimeOrder={basicInfo.isFirstTimeOrder ?? false}
          hasChildOrders={basicInfo.hasChildOrders}
          status={basicInfo.status}
          createdAt={basicInfo.createdAt}
          finishedAt={basicInfo.finishedAt}
          canceledAt={basicInfo.canceledAt}
          cancelReason={basicInfo.cancelReason}
          taskNo={basicInfo.taskNo}
          orderNo={basicInfo.orderNo}
          licensePlateNo={basicInfo.licensePlateNo}
          vin={basicInfo.vin}
          chassisNo={basicInfo.chassisNo}
          serviceAgentName={basicInfo.serviceAgentName}
          serviceAgentMemberId={basicInfo.serviceAgentId}
          storeName={store?.name}
          vehicleMileage={basicInfo.vehicleMileage}
          vehicleName={basicInfo.vehicleName}
          vehicleBrandLogo={basicInfo.vehicleBrandLogo}
          vehicleFuelType={basicInfo.vehicleFuelType}
          customerSigned={basicInfo.signImgUrl != null}
          customerType={basicInfo.customerType}
          customerName={basicInfo.customerName}
          customerMobile={basicInfo.customerMobile}
          returnType={basicInfo.returnType}
          estimatedFinishTime={basicInfo.estimatedFinishTime}
          insuranceExpiryDate={basicInfo.insuranceExpiryDate}
          tags={basicInfo.tags}
          subscribed={subscribed}
          serviceRecordCount={serviceRecordCount ?? 0}
          brandLogoPosition="right"
          hideExtendedProps={hideExtendedProps}
          isRollback={basicInfo.isRollback}
          rollbackAt={basicInfo.rollbackAt}
          rollbackReason={basicInfo.rollbackReason}
          carwashType={basicInfo.carwashType}
          oldPartDisposalType={basicInfo.oldPartDisposalType}
          reservationType={basicInfo.reservationType}
          onEditPress={onEditPress}
          onShowDetail={onShowDetail}
          onQrcodeSharingPress={onQrcodeSharingPress}
          onServiceRecordsPress={onServiceRecordsPress}
        />
      </>
    );
  },
);
